// Core Game Engine - Main game loop and rendering system
import { Renderer } from './Renderer.js';
import { InputManager } from './InputManager.js';
import { AudioManager } from './AudioManager.js';
import { AssetManager } from './AssetManager.js';
import { PerformanceMonitor } from './PerformanceMonitor.js';

export class GameEngine {
    constructor(gameConfig) {
        this.config = gameConfig;
        this.canvas = null;
        this.ctx = null;
        this.renderer = null;
        this.inputManager = null;
        this.audioManager = null;
        this.assetManager = null;
        this.performanceMonitor = null;
        
        // Game state
        this.isRunning = false;
        this.isPaused = false;
        this.lastTime = 0;
        this.deltaTime = 0;
        this.currentTime = 0;
        
        // Game objects
        this.gameObjects = new Map();
        this.gameInstance = null;
        
        // Performance
        this.targetFPS = 60;
        this.frameTime = 1000 / this.targetFPS;
        
        this.init();
    }

    async init() {
        console.log('🔧 Initializing Game Engine...');
        
        // Get canvas element
        this.canvas = document.getElementById('game-canvas');
        if (!this.canvas) {
            throw new Error('Game canvas not found');
        }
        
        // Initialize core systems
        this.renderer = new Renderer(this.canvas);
        this.inputManager = new InputManager(this.canvas);
        this.audioManager = new AudioManager();
        this.assetManager = new AssetManager();
        this.performanceMonitor = new PerformanceMonitor();
        
        // Setup canvas
        this.setupCanvas();
        
        // Load game assets
        await this.loadAssets();
        
        // Initialize game instance
        await this.initializeGame();
        
        console.log('✅ Game Engine initialized successfully!');
    }

    setupCanvas() {
        // Set canvas size based on container
        this.resizeCanvas();
        
        // Add resize listener
        window.addEventListener('resize', () => this.resizeCanvas());
        
        // Setup high DPI support
        const dpr = window.devicePixelRatio || 1;
        const rect = this.canvas.getBoundingClientRect();
        
        this.canvas.width = rect.width * dpr;
        this.canvas.height = rect.height * dpr;
        
        this.ctx = this.canvas.getContext('2d');
        this.ctx.scale(dpr, dpr);
        
        // Enable image smoothing for better quality
        this.ctx.imageSmoothingEnabled = true;
        this.ctx.imageSmoothingQuality = 'high';
    }

    resizeCanvas() {
        const container = this.canvas.parentElement;
        const containerWidth = container.clientWidth;
        const containerHeight = container.clientHeight;
        
        // Maintain aspect ratio (16:9)
        const aspectRatio = 16 / 9;
        let width = containerWidth;
        let height = width / aspectRatio;
        
        if (height > containerHeight) {
            height = containerHeight;
            width = height * aspectRatio;
        }
        
        this.canvas.style.width = `${width}px`;
        this.canvas.style.height = `${height}px`;
    }

    async loadAssets() {
        console.log('📦 Loading game assets...');
        
        try {
            // Load assets based on game configuration
            const assets = this.config.assets || {};
            
            // Load images
            if (assets.images) {
                for (const [key, src] of Object.entries(assets.images)) {
                    await this.assetManager.loadImage(key, src);
                }
            }
            
            // Load audio
            if (assets.audio) {
                for (const [key, src] of Object.entries(assets.audio)) {
                    await this.assetManager.loadAudio(key, src);
                }
            }
            
            console.log('✅ Assets loaded successfully!');
            
        } catch (error) {
            console.error('❌ Failed to load assets:', error);
            throw error;
        }
    }

    async initializeGame() {
        console.log('🎮 Initializing game instance...');
        
        try {
            // Dynamically import the game class
            const gameModule = await import(`../games/${this.config.className}.js`);
            const GameClass = gameModule[this.config.className];
            
            if (!GameClass) {
                throw new Error(`Game class ${this.config.className} not found`);
            }
            
            // Create game instance
            this.gameInstance = new GameClass({
                canvas: this.canvas,
                ctx: this.ctx,
                renderer: this.renderer,
                inputManager: this.inputManager,
                audioManager: this.audioManager,
                assetManager: this.assetManager
            });
            
            // Initialize game
            if (this.gameInstance.init) {
                await this.gameInstance.init();
            }
            
            console.log(`✅ Game ${this.config.name} initialized successfully!`);
            
        } catch (error) {
            console.error('❌ Failed to initialize game:', error);
            throw error;
        }
    }

    start() {
        if (this.isRunning) return;
        
        console.log('🚀 Starting game loop...');
        this.isRunning = true;
        this.isPaused = false;
        this.lastTime = performance.now();
        
        this.gameLoop();
    }

    stop() {
        console.log('⏹️ Stopping game loop...');
        this.isRunning = false;
        this.isPaused = false;
    }

    pause() {
        if (!this.isRunning || this.isPaused) return;
        
        console.log('⏸️ Pausing game...');
        this.isPaused = true;
        
        if (this.gameInstance && this.gameInstance.onPause) {
            this.gameInstance.onPause();
        }
    }

    resume() {
        if (!this.isRunning || !this.isPaused) return;
        
        console.log('▶️ Resuming game...');
        this.isPaused = false;
        this.lastTime = performance.now();
        
        if (this.gameInstance && this.gameInstance.onResume) {
            this.gameInstance.onResume();
        }
    }

    gameLoop() {
        if (!this.isRunning) return;
        
        this.currentTime = performance.now();
        this.deltaTime = this.currentTime - this.lastTime;
        
        // Cap delta time to prevent large jumps
        const cappedDeltaTime = Math.min(this.deltaTime, this.frameTime * 3);
        
        if (!this.isPaused) {
            // Update game logic
            this.update(cappedDeltaTime / 1000); // Convert to seconds
            
            // Render frame
            this.render();
        }
        
        // Update performance monitor
        this.performanceMonitor.update(this.deltaTime);
        
        // Update UI
        this.updateUI();
        
        this.lastTime = this.currentTime;
        requestAnimationFrame(() => this.gameLoop());
    }

    update(deltaTime) {
        try {
            // Update game instance
            if (this.gameInstance && this.gameInstance.update) {
                this.gameInstance.update(deltaTime);
            }
            
            // Update all game objects
            for (const gameObject of this.gameObjects.values()) {
                if (gameObject.update) {
                    gameObject.update(deltaTime);
                }
            }
            
        } catch (error) {
            console.error('❌ Error during game update:', error);
            this.handleError(error);
        }
    }

    render() {
        try {
            // Clear canvas
            this.renderer.clear('#000011');
            
            // Render game instance
            if (this.gameInstance && this.gameInstance.render) {
                this.gameInstance.render(this.renderer);
            }
            
            // Render all game objects
            for (const gameObject of this.gameObjects.values()) {
                if (gameObject.render) {
                    gameObject.render(this.renderer);
                }
            }
            
            // Render performance overlay if enabled
            if (this.performanceMonitor.isEnabled()) {
                this.performanceMonitor.render(this.renderer);
            }
            
        } catch (error) {
            console.error('❌ Error during game render:', error);
            this.handleError(error);
        }
    }

    updateUI() {
        // Update FPS counter
        const fps = this.performanceMonitor.getFPS();
        const fpsElement = document.getElementById('game-fps');
        if (fpsElement) {
            fpsElement.textContent = `FPS: ${Math.round(fps)}`;
        }
        
        // Update game stats
        if (this.gameInstance && this.gameInstance.getStats) {
            const stats = this.gameInstance.getStats();
            const scoreElement = document.getElementById('game-score');
            if (scoreElement && stats.score !== undefined) {
                scoreElement.textContent = `Score: ${stats.score}`;
            }
        }
    }

    handleResize() {
        if (this.renderer) {
            this.renderer.handleResize();
        }
        
        if (this.gameInstance && this.gameInstance.handleResize) {
            this.gameInstance.handleResize();
        }
    }

    handleError(error) {
        console.error('Game Engine Error:', error);
        
        // Stop the game loop
        this.stop();
        
        // Show error to user
        if (this.uiManager) {
            this.uiManager.showError(`Game Error: ${error.message}`);
        }
    }

    // Public API methods
    addGameObject(id, gameObject) {
        this.gameObjects.set(id, gameObject);
    }

    removeGameObject(id) {
        this.gameObjects.delete(id);
    }

    getGameObject(id) {
        return this.gameObjects.get(id);
    }

    getCanvas() {
        return this.canvas;
    }

    getContext() {
        return this.ctx;
    }

    getRenderer() {
        return this.renderer;
    }

    getInputManager() {
        return this.inputManager;
    }

    getAudioManager() {
        return this.audioManager;
    }

    getAssetManager() {
        return this.assetManager;
    }

    getPerformanceMonitor() {
        return this.performanceMonitor;
    }

    isGameRunning() {
        return this.isRunning;
    }

    isGamePaused() {
        return this.isPaused;
    }
}